import React, { useRef, useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import { ReactComponent as Upload } from "../../assets/AccountingModule/upload.svg";
import styles from "./style";

const DocumentUpload = ({ 
  handleUrl, 
  isFrom, 
  image, 
  handleimage, 
  fileName, 
  disabled = false,
  accept = {
    'application/pdf': ['.pdf'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
  },
  // These props are passed from parent components but not used internally
  // They're accepted for backward compatibility
  name,
  docs,
  setDocs,
  value,
  onChange,
  ...otherProps
}) => {
  const [imageUrl, setImageUrl] = useState(image || null);
  const inputRef = useRef(null);

  // Sync image prop with imageUrl state
  useEffect(() => {
    if (image) {
      setImageUrl(image);
    } else {
      setImageUrl(null);
    }
  }, [image]);

  // Cleanup object URLs to prevent memory leaks
  useEffect(() => {
    return () => {
      if (imageUrl && imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  // This function is triggered when files are dropped or selected
  const onDrop = (acceptedFiles) => {
    if (disabled) return;
    if (!acceptedFiles?.length) return;
    
    const file = acceptedFiles[0];
    
    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file);
    setImageUrl(objectUrl);
    
    // Call handleimage with the file object (required for upload)
    if (handleimage && typeof handleimage === 'function') {
      handleimage(file);
    }
    
    // Call handleUrl with filename if provided (optional, for backward compatibility)
    if (handleUrl && typeof handleUrl === 'function') {
      handleUrl(file.name);
    }
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled,
    accept,
    multiple: false,
    noClick: false,
    noKeyboard: false,
  });

  // Get input props and merge ref
  const inputProps = getInputProps();
  const mergedInputProps = {
    ...inputProps,
    ref: (node) => {
      inputRef.current = node;
      // Call the original ref callback if it exists
      const originalRef = inputProps.ref;
      if (originalRef) {
        if (typeof originalRef === 'function') {
          originalRef(node);
        } else if (originalRef && typeof originalRef === 'object') {
          originalRef.current = node;
        }
      }
    }
  };

  const handleImageClick = (e) => {
    if (disabled) return;
    e.stopPropagation();
    e.preventDefault();
    // Use the dropzone's open function or click the input directly
    if (open) {
      open();
    } else if (inputRef.current) {
      inputRef.current.click();
    }
  };

  // Determine if the file is an image or PDF/document
  const isImageFile = imageUrl && (
    imageUrl.startsWith('data:image') || 
    imageUrl.includes('.jpg') || 
    imageUrl.includes('.jpeg') || 
    imageUrl.includes('.png') || 
    imageUrl.includes('.gif')
  );

  const isPdfFile = imageUrl && (
    imageUrl.includes('.pdf') || 
    imageUrl.includes('application/pdf')
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        {...getRootProps()}
        sx={{
          width: "100%",
          border: "1px dashed #ccc",
          borderRadius: "5px",
          py: isFrom !== "edit" && 2,
          px: 1,
          mb: 1,
          minHeight: isFrom === "edit" ? "150px" : "200px",
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          flexDirection: "column",
          cursor: disabled ? "not-allowed" : "pointer",
          bgcolor: disabled ? "#f5f5f5" : isDragActive ? "#eee" : "transparent",
          borderColor: disabled ? "#d1d5db" : "#ccc",
          "&:hover": disabled ? {} : {
            borderColor: "#28AA4A",
            bgcolor: "#f0f9f4"
          }
        }}
      >
        <input
          {...mergedInputProps}
          style={{ display: "none" }}
        />

        {!imageUrl ? (
          // When no file is uploaded - dropzone handles clicks via getRootProps
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "150px",
            }}
          >
            <Box sx={styles.chooseIconSection}>
              <Box sx={{ mr: 1 }}>
                <Upload />
              </Box>
              <Typography sx={styles.choose}>Upload File</Typography>
            </Box>
            <Typography sx={{ fontSize: "12px", color: "#666", mt: 1 }}>
              Drag & drop or click to select
            </Typography>
          </Box>
        ) : (
          // When a file is uploaded, show preview
          <Box
            sx={{
              ...styles.selectedImage,
              width: "100%",
              minHeight: "150px",
              position: "relative",
            }}
            onClick={handleImageClick}
            style={{ pointerEvents: "auto" }}
          >
            {isImageFile ? (
              <img
                src={imageUrl}
                alt={fileName || "Uploaded file"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "5px",
                  cursor: disabled ? "default" : "pointer",
                }}
              />
            ) : isPdfFile ? (
              <iframe
                src={imageUrl}
                width="100%"
                height="100%"
                style={{
                  borderRadius: "5px",
                  cursor: disabled ? "default" : "pointer",
                  minHeight: "150px",
                  border: "none"
                }}
                title={fileName || "PDF Preview"}
                loading="lazy"
              />
            ) : (
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "150px",
                }}
              >
                <Typography sx={{ fontSize: "14px", color: "#666" }}>
                  {fileName || "File uploaded"}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#999", mt: 1 }}>
                  Click to change file
                </Typography>
              </Box>
            )}
            {!disabled && (
              <Typography
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "white",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  padding: "4px 12px",
                  borderRadius: "4px",
                  fontSize: "12px",
                  pointerEvents: "none",
                }}
              >
                Click to Re-upload
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DocumentUpload;
